import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Container,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';

const ResetPasswordConfirm: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenVerified, setTokenVerified] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // トークンの検証
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('無効なリセットトークンです');
        setVerifying(false);
        return;
      }

      try {
        await axios.get(`${config.API_BASE_URL}/auth/verify-reset-token/${token}`);
        setTokenVerified(true);
      } catch (err: any) {
        setError(err.response?.data?.message || 'リセットトークンが無効です');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (password.length < 8) {
      setError('パスワードは8文字以上必要です');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post(`${config.API_BASE_URL}/auth/reset-password`, {
        token,
        new_password: password
      });
      setSuccess(true);
      
      // 3秒後にログインページにリダイレクト
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'パスワードの変更に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" component="h1" gutterBottom align="center">
            新しいパスワードの設定
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!tokenVerified ? (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                パスワードリセットリンクが無効か期限切れです。もう一度リセットをリクエストしてください。
              </Alert>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link to="/password-reset" style={{ textDecoration: 'none' }}>
                  <Button variant="contained" color="primary">
                    パスワードリセットに戻る
                  </Button>
                </Link>
              </Box>
            </Box>
          ) : success ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                パスワードが正常に変更されました。3秒後にログインページにリダイレクトします。
              </Alert>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Button variant="contained" color="primary">
                    ログインページへ
                  </Button>
                </Link>
              </Box>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <Typography variant="body2" color="textSecondary" paragraph>
                新しいパスワードを入力してください。
              </Typography>

              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="新しいパスワード"
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="新しいパスワード（確認）"
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'パスワードを変更'}
              </Button>
            </form>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordConfirm; 